import { useSession } from "@clerk/clerk-react";
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@heroui/react";
import { Check } from "@phosphor-icons/react";
import { AlertCircle } from "assets/icons";
import ButtonNew from "components/buttonNew/buttonNew";
import { API_URL } from "constants/index";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { RentalContract } from "shared/types/rental-contracts";

export interface ActiveContractModalProps {
    isOpen: boolean;
    onClose?: () => void;
    incomingContract: RentalContract | null;
    handleContractRefresh: () => void;
}

export const ActivateContractModal = ({ isOpen, onClose, incomingContract, handleContractRefresh }: ActiveContractModalProps) => {
    const [selectedContract, setSelectedContract] = useState<RentalContract | null>(null);
    const {session} = useSession();

    useEffect(() => {
        setSelectedContract(incomingContract);
    }, [incomingContract]);

    // Calculate next invoice date (first day of next month)
    const getNextInvoiceDate = () => {
        const nextMonth = new Date();
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        nextMonth.setDate(1);
        nextMonth.setHours(0, 0, 0, 0);
        return nextMonth;
    };

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('nl-NL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const activateContract = async () => {
        console.log('Activate contract', selectedContract);

        try {
            await fetchWithAuth(`${API_URL}/rental/contract/${selectedContract?.id}/activate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',  
                },
                body: JSON.stringify({}),
            }, session);
    
            toast.success('Contract geactiveerd');
        } catch (error) {
            console.error('Error activating contract', error);
            toast.error('Fout bij het activeren van het contract');
        } finally {
            setSelectedContract(null);
            handleContractRefresh();
            handleCLose();
        }
    };

    const handleCLose = () => {
        setSelectedContract(null);
        onClose && onClose();
    }

    const nextInvoiceDate = getNextInvoiceDate();

    return (
        <Modal onClose={handleCLose} isOpen={isOpen} isDismissable={true} isKeyboardDismissDisabled={true} className="font-normal text-neutral-primary" size="4xl" scrollBehavior="inside">
            <ModalContent>
                {(onClose) => (
                    <>
                        <ModalHeader className="flex flex-col gap-1">
                            <p className="flex items-center gap-1 text-sm font-medium text-status-bad">
                                <AlertCircle className="h-[17px] w-[17px]" /> Activeer contract
                            </p>
                        </ModalHeader>
                        <ModalBody>
                            <p className="text-neutral-primary mb-4">Ben je zeker dat je dit contract wil activeren?</p>
                            
                            <div className="bg-moss-primary/10 border border-moss-primary/30 rounded-lg p-4 mb-4">
                                <p className="text-moss-primary font-medium mb-2">
                                    De eerstvolgende factuur zal worden uitgestuurd op {formatDate(nextInvoiceDate)}
                                </p>
                                <p className="text-neutral-secondary-mid text-sm">
                                    Als er tussentijds nog een factuur moet vertrekken, moet die handmatig in de boekhouding worden aangemaakt.
                                </p>
                            </div>
                        </ModalBody>
                        <ModalFooter className="justify-start">
                            <ButtonNew
                                variant="primaryStatusGreen"
                                onClick={activateContract}
                                icon={<Check />}
                                label="Activeer contract"
                            />
                            <ButtonNew
                                variant="secondaryNeutral"
                                onClick={onClose}
                                label="Annuleren"
                            />
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
};
